import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";
import {
  Loader2,
  Layers,
  Save,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  FileText,
  Plus,
  Trash2,
  Eye,
  Shapes,
} from "lucide-react";
import { toast } from "sonner";

import { GET_PROMPT_LAYERS, GET_SHAPE_PACKS } from "@/graphql/queries/admin";
import { SAVE_PROMPT_LAYER, SAVE_SHAPE_PACK } from "@/graphql/mutations/admin";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface LayerInfo {
  key: string;
  label: string;
  filename: string;
  path: string;
  exists: boolean;
  content: string | null;
}

interface PromptLayersData {
  layers: LayerInfo[];
  composed: string | null;
  shape_packs?: { content: string }[];
}

interface ShapePackInfo {
  filename: string;
  path: string;
  content: string;
}

type Tab = "layers" | "shape-packs";

const SUBJECTS = [
  { value: "math", label: "Matematika" },
  { value: "lithuanian", label: "Lietuviu kalba" },
  { value: "english", label: "Anglu kalba" },
  { value: "physics", label: "Fizika" },
  { value: "chemistry", label: "Chemija" },
  { value: "geography", label: "Geografija" },
];

const GRADES = Array.from({ length: 12 }, (_, i) => i + 1);
const LEVELS = [
  { value: 1, label: "1 - Lengvas" },
  { value: 2, label: "2 - Vidutinis" },
  { value: 3, label: "3 - Sunkus" },
];

export default function PromptLayersPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [subject, setSubject] = useState(searchParams.get("subject") || "math");
  const [grade, setGrade] = useState(searchParams.get("grade") || "1");
  const [level, setLevel] = useState(searchParams.get("level") || "");

  const [layersData, setLayersData] = useState<PromptLayersData | null>(null);
  const [loading, setLoading] = useState(false);

  // Track which layer is being edited
  const [editingLayer, setEditingLayer] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");
  const [originalContent, setOriginalContent] = useState<string | null>(null);

  // Show composed view
  const [showComposed, setShowComposed] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState<Tab>("layers");

  // Shape packs state
  const [editingPack, setEditingPack] = useState<string | null>(null);
  const [packEditContent, setPackEditContent] = useState("");
  const [packOriginalContent, setPackOriginalContent] = useState<string | null>(null);

  const [fetchLayers] = useLazyQuery(GET_PROMPT_LAYERS, {
    fetchPolicy: "network-only",
  });
  const [saveLayer, { loading: saving }] = useMutation(SAVE_PROMPT_LAYER);

  // Shape packs queries/mutations
  const { data: shapePacksData, refetch: refetchShapePacks } = useQuery(GET_SHAPE_PACKS, {
    fetchPolicy: "network-only",
  });
  const [saveShapePack, { loading: savingPack }] = useMutation(SAVE_SHAPE_PACK);

  const loadLayers = useCallback(async () => {
    setLoading(true);
    try {
      const vars: Record<string, string | number> = { subject };
      if (grade) vars.grade = parseInt(grade);
      if (level && level !== "all") vars.level = parseInt(level);

      const { data } = await fetchLayers({ variables: vars });
      const result = data?.promptLayers
        ? typeof data.promptLayers === "string"
          ? JSON.parse(data.promptLayers)
          : data.promptLayers
        : null;
      setLayersData(result);
    } catch {
      toast.error("Nepavyko užkrauti sluoksnių");
    } finally {
      setLoading(false);
    }
  }, [subject, grade, level, fetchLayers]);

  useEffect(() => {
    loadLayers();
  }, [loadLayers]);

  // Update URL params
  useEffect(() => {
    const params: Record<string, string> = { subject };
    if (grade) params.grade = grade;
    if (level && level !== "all") params.level = level;
    setSearchParams(params, { replace: true });
  }, [subject, grade, level, setSearchParams]);

  const handleEditLayer = (layer: LayerInfo) => {
    setEditingLayer(layer.key);
    setEditContent(layer.content || "");
    setOriginalContent(layer.content || null);
    setShowComposed(false);
  };

  const handleCreateLayer = (layer: LayerInfo) => {
    setEditingLayer(layer.key);
    setEditContent("");
    setOriginalContent(null);
    setShowComposed(false);
  };

  const handleSave = async () => {
    if (!editingLayer) return;

    const vars: Record<string, string | number> = {
      layerKey: editingLayer,
      content: editContent,
    };
    if (subject) vars.subject = subject;
    if (grade) vars.grade = parseInt(grade);
    if (level && level !== "all") vars.level = parseInt(level);

    try {
      const { data } = await saveLayer({ variables: vars });
      if (data?.savePromptLayer?.success) {
        toast.success("Sluoksnis išsaugotas");
        setOriginalContent(editContent || null);
        await loadLayers();
      } else {
        toast.error(data?.savePromptLayer?.error || "Klaida");
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Klaida");
    }
  };

  const handleDelete = async (layer: LayerInfo) => {
    const vars: Record<string, string | number> = {
      layerKey: layer.key,
      content: "",
    };
    if (subject) vars.subject = subject;
    if (grade) vars.grade = parseInt(grade);
    if (level && level !== "all") vars.level = parseInt(level);

    try {
      const { data } = await saveLayer({ variables: vars });
      if (data?.savePromptLayer?.success) {
        toast.success("Sluoksnis ištrintas");
        if (editingLayer === layer.key) {
          setEditingLayer(null);
        }
        await loadLayers();
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Klaida");
    }
  };

  // Shape pack handlers
  const handleEditPack = (pack: ShapePackInfo) => {
    setEditingPack(pack.filename);
    setPackEditContent(pack.content);
    setPackOriginalContent(pack.content);
  };

  const handleSavePack = async () => {
    if (!editingPack) return;
    try {
      const { data } = await saveShapePack({
        variables: { filename: editingPack, content: packEditContent },
      });
      if (data?.saveShapePack?.success) {
        toast.success("Shape pack išsaugotas");
        setPackOriginalContent(packEditContent);
        refetchShapePacks();
      } else {
        toast.error(data?.saveShapePack?.error || "Klaida");
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Klaida");
    }
  };

  const handleDeletePack = async (filename: string) => {
    try {
      const { data } = await saveShapePack({
        variables: { filename, content: "" },
      });
      if (data?.saveShapePack?.success) {
        toast.success("Shape pack ištrintas");
        if (editingPack === filename) setEditingPack(null);
        refetchShapePacks();
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Klaida");
    }
  };

  const editModified =
    editingLayer !== null &&
    editContent !== (originalContent ?? "");

  const packModified =
    editingPack !== null &&
    packEditContent !== (packOriginalContent ?? "");

  const existingLayers = layersData?.layers.filter((l) => l.exists) || [];
  const availableLayers = layersData?.layers.filter((l) => !l.exists) || [];

  const shapePacks: ShapePackInfo[] = (() => {
    const raw = shapePacksData?.shapePacks;
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    return parsed?.packs || [];
  })();

  return (
    <div className="space-y-6 max-w-6xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/admin/exercises">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Atgal
          </Link>
        </Button>
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Layers className="h-5 w-5" />
            Prompt sluoksniai
          </h1>
          <p className="text-sm text-muted-foreground">
            Sukurkite bendrus prompt fragmentus, kurie bus pridėti prie visų
            atitinkamų užduočių
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Dalykas</Label>
              <Select value={subject} onValueChange={setSubject}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUBJECTS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Klasė</Label>
              <Select value={grade} onValueChange={setGrade}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GRADES.map((g) => (
                    <SelectItem key={g} value={String(g)}>
                      {g} klasė
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Lygis (neprivalomas)</Label>
              <Select value={level} onValueChange={setLevel}>
                <SelectTrigger>
                  <SelectValue placeholder="Visi lygiai" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Visi lygiai</SelectItem>
                  {LEVELS.map((l) => (
                    <SelectItem key={l.value} value={String(l.value)}>
                      {l.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "layers"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setActiveTab("layers")}
        >
          <Layers className="h-3.5 w-3.5 inline mr-1.5" />
          Sluoksniai
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "shape-packs"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setActiveTab("shape-packs")}
        >
          <Shapes className="h-3.5 w-3.5 inline mr-1.5" />
          SVG Shape Packs ({shapePacks.length})
        </button>
      </div>

      {activeTab === "layers" ? (
        /* Layers tab content */
        loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">
              Kraunama...
            </span>
          </div>
        ) : (
          <div className="grid grid-cols-[350px_1fr] gap-6">
            {/* Left: Layer list */}
            <div className="space-y-4">
              {/* Existing layers */}
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-2">
                  Aktyvūs sluoksniai ({existingLayers.length})
                </h3>
                {existingLayers.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-3 px-3 border rounded-md">
                    Nėra sukurtų sluoksnių. Pasirinkite iš galimų sluoksnių
                    žemiau.
                  </p>
                ) : (
                  <div className="space-y-1">
                    {existingLayers.map((layer) => (
                      <LayerItem
                        key={layer.key}
                        layer={layer}
                        active={editingLayer === layer.key}
                        onEdit={() => handleEditLayer(layer)}
                        onDelete={() => handleDelete(layer)}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Available (empty) layers */}
              {availableLayers.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">
                    Galimi sluoksniai
                  </h3>
                  <div className="space-y-1">
                    {availableLayers.map((layer) => (
                      <button
                        key={layer.key}
                        onClick={() => handleCreateLayer(layer)}
                        className={`
                          w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left
                          border border-dashed hover:bg-accent hover:text-accent-foreground
                          transition-colors
                          ${editingLayer === layer.key ? "bg-accent border-solid" : ""}
                        `}
                      >
                        <Plus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <div className="min-w-0">
                          <div className="font-medium truncate">
                            {layer.label}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {layer.filename}.md
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Composed view toggle */}
              {existingLayers.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    setShowComposed(!showComposed);
                    setEditingLayer(null);
                  }}
                >
                  <Eye className="h-3.5 w-3.5 mr-2" />
                  {showComposed
                    ? "Slesti sujungta vaizdas"
                    : "Rodyti sujungta vaizdas"}
                </Button>
              )}
            </div>

            {/* Right: Editor / Composed view */}
            <div>
              {showComposed ? (
                <Card>
                  <CardContent className="pt-4 pb-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-medium">
                        Sujungtas prompt (visi sluoksniai)
                      </Label>
                      <Badge variant="secondary">
                        {existingLayers.length}{" "}
                        {existingLayers.length === 1
                          ? "sluoksnis"
                          : "sluoksniai"}
                      </Badge>
                    </div>
                    <Textarea
                      value={layersData?.composed || "(tuščia)"}
                      readOnly
                      rows={30}
                      className="font-mono text-xs bg-muted/50"
                    />
                  </CardContent>
                </Card>
              ) : editingLayer ? (
                <Card>
                  <CardContent className="pt-4 pb-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base font-medium">
                          {layersData?.layers.find(
                            (l) => l.key === editingLayer
                          )?.label || editingLayer}
                        </Label>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {layersData?.layers.find(
                            (l) => l.key === editingLayer
                          )?.filename || ""}
                          .md
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {editModified && (
                          <span className="text-xs text-orange-600 dark:text-orange-400">
                            Pakeistas
                          </span>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleSave}
                          disabled={saving || !editModified}
                        >
                          {saving ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                          ) : (
                            <Save className="h-3.5 w-3.5 mr-1" />
                          )}
                          Išsaugoti
                        </Button>
                      </div>
                    </div>
                    <Textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      rows={24}
                      className="font-mono text-xs"
                      placeholder="Įveskite prompt fragmentą... Šis tekstas bus pridėtas prie visų atitinkamų užduočių prompt."
                    />
                    <p className="text-xs text-muted-foreground">
                      Galite naudoti placeholder: {"{{LEVEL}}"}, {"{{LEVEL_NAME}}"},
                      {"{{COUNT}}"}, {"{{HINT_INSTRUCTION}}"}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="flex items-center justify-center py-24 text-muted-foreground text-sm border rounded-md border-dashed">
                  Pasirinkite sluoksnį iš sąrašo kairėje arba sukurkite naują
                </div>
              )}
            </div>
          </div>
        )
      ) : (
        /* Shape Packs tab content */
        <div className="grid grid-cols-[350px_1fr] gap-6">
          {/* Left: Shape pack list */}
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-2">
                Shape Pack failai ({shapePacks.length})
              </h3>
              <p className="text-xs text-muted-foreground mb-3">
                SVG figūrų dokumentacija, kuri automatiškai pridedama prie AI prompt pagal įgūdžio kategoriją ir klasės lygį.
              </p>
              {shapePacks.length === 0 ? (
                <p className="text-xs text-muted-foreground py-3 px-3 border rounded-md">
                  Nėra shape pack failų. Paleiskite{" "}
                  <code className="bg-muted px-1 rounded">rake svg:catalog</code>{" "}
                  kad sugeneruotumėte pradinius failus.
                </p>
              ) : (
                <div className="space-y-1">
                  {shapePacks.map((pack) => (
                    <div
                      key={pack.filename}
                      className={`
                        flex items-center gap-2 px-3 py-2 rounded-md text-sm border
                        transition-colors cursor-pointer group
                        ${editingPack === pack.filename ? "bg-accent border-primary/30" : "hover:bg-accent/50"}
                      `}
                      onClick={() => handleEditPack(pack)}
                    >
                      <Shapes className="h-3.5 w-3.5 shrink-0 text-primary" />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{pack.filename}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {pack.content.split("\n").length} eilučių
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeletePack(pack.filename);
                        }}
                        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-destructive/10 hover:text-destructive transition-opacity"
                        title="Ištrinti"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="text-xs text-muted-foreground p-3 border rounded-md space-y-1">
              <p className="font-medium">Konvencija:</p>
              <p><code>{"{kategorija}"}.md</code> - bazinis paketas</p>
              <p><code>{"{kategorija}"}.elementary.md</code> — 1–4 klasė</p>
              <p><code>{"{kategorija}"}.middle.md</code> — 5–8 klasė</p>
              <p><code>{"{kategorija}"}.high.md</code> — 9–12 klasė</p>
              <p className="pt-1">
                Kategorijos: geometry, time-reading, data-visualization
              </p>
            </div>
          </div>

          {/* Right: Shape pack editor */}
          <div>
            {editingPack ? (
              <Card>
                <CardContent className="pt-4 pb-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base font-medium">
                        {editingPack}
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        content/prompts/shapes/{editingPack}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {packModified && (
                        <span className="text-xs text-orange-600 dark:text-orange-400">
                          Pakeistas
                        </span>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleSavePack}
                        disabled={savingPack || !packModified}
                      >
                        {savingPack ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                        ) : (
                          <Save className="h-3.5 w-3.5 mr-1" />
                        )}
                        Išsaugoti
                      </Button>
                    </div>
                  </div>
                  <Textarea
                    value={packEditContent}
                    onChange={(e) => setPackEditContent(e.target.value)}
                    rows={24}
                    className="font-mono text-xs"
                    placeholder="Shape pack turinys (Markdown formatu)..."
                  />
                  <p className="text-xs text-muted-foreground">
                    Naudokite <code>&lt;img src="/svg/tipas?parametrai" /&gt;</code> pavyzdžiams.
                    Šis turinys bus automatiškai įterptas į AI prompt.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="flex items-center justify-center py-24 text-muted-foreground text-sm border rounded-md border-dashed">
                Pasirinkite shape pack iš sąrašo kairėje
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function LayerItem({
  layer,
  active,
  onEdit,
  onDelete,
}: {
  layer: LayerInfo;
  active: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      className={`
        flex items-center gap-2 px-3 py-2 rounded-md text-sm border
        transition-colors cursor-pointer group
        ${active ? "bg-accent border-primary/30" : "hover:bg-accent/50"}
      `}
      onClick={onEdit}
    >
      <FileText className="h-3.5 w-3.5 shrink-0 text-primary" />
      <div className="min-w-0 flex-1">
        <div className="font-medium truncate">{layer.label}</div>
        <div className="text-xs text-muted-foreground truncate">
          {layer.filename}.md
        </div>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-destructive/10 hover:text-destructive transition-opacity"
        title="Ištrinti sluoksnį"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
